import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { Input, Button } from 'reactstrap'
import {
  mapTenantToCw,
  searchCompanies,
} from '../../actions/cspRenewalsActions'

// Single search input. Backend returns a unified, deduped list of companies
// ordered Managed first, then Essentials, then raw CW - admin never sees the
// distinction. Clicking any row dispatches mapTenantToCw with the chosen
// company's CW int; server-side reuse-first picks the right backing Guid
// (existing reconciliation, existing shadow, or a freshly-created shadow).

const useDebounced = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(handle)
  }, [value, delayMs])
  return debounced
}

const TenantMappingPicker = ({
  ddTenantId,
  includeInactive = false,
  submitLabel = 'Map',
  onMapped,
  onCancel,
}) => {
  const dispatch = useDispatch()
  const updatingTenantId = useSelector((s) => s.cspRenewals.updatingTenantId)
  // Scope the busy state to THIS picker's tenant. updatingTenantId is a
  // single-slot field that surfaces whichever tenant is currently being
  // mutated anywhere on the page - using !! would freeze every picker
  // instance when an unrelated row (e.g. a settings drawer save) is in
  // flight.
  const isUpdating = updatingTenantId === ddTenantId

  const [searchText, setSearchText] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  // Distinct from "results.length > 0" - tracks whether we've completed at
  // least one search for the current query. Lets us show "No matches" once
  // a search has settled with zero results, without flashing it before
  // the user finishes typing.
  const [hasSearched, setHasSearched] = useState(false)
  const [pickedCwId, setPickedCwId] = useState(null)
  const debounced = useDebounced(searchText, 250)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!debounced.trim()) { setResults([]); setHasSearched(false); return }
      setSearching(true)
      const rows = await dispatch(searchCompanies(debounced, 20))
      if (!cancelled) {
        setResults(rows || [])
        setSearching(false)
        setHasSearched(true)
      }
    }
    run()
    return () => { cancelled = true }
  }, [dispatch, debounced])

  const handleMap = async () => {
    if (!pickedCwId || !ddTenantId) return
    await dispatch(mapTenantToCw(ddTenantId, pickedCwId, includeInactive))
    onMapped?.()
  }

  return (
    <div className="d-flex" style={{ gap: '0.5rem' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <Input
          type="text"
          placeholder="Search companies..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setPickedCwId(null) }}
          bsSize="sm"
          disabled={isUpdating}
        />
        {searchText && !pickedCwId && (searching || hasSearched) && (
          <div
            className="border rounded bg-white shadow-sm"
            style={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: '280px', overflowY: 'auto' }}
          >
            {searching ? (
              <div className="p-2 small text-muted">
                <span className="spinner-border spinner-border-sm mr-2" />
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className="p-2 small text-muted">No matches.</div>
            ) : (
              results.map((c) => (
                <div
                  key={c.cwCompanyId}
                  className="p-2 border-bottom"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setPickedCwId(c.cwCompanyId)
                    setSearchText(c.name)
                  }}
                >
                  <small>
                    <strong>{c.name}</strong>
                    {c.identifier && <span className="text-muted ml-2">#{c.identifier}</span>}
                  </small>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <Button
        size="sm"
        color="primary"
        onClick={handleMap}
        disabled={!pickedCwId || isUpdating}
      >
        {isUpdating ? <span className="spinner-border spinner-border-sm" /> : submitLabel}
      </Button>
      {onCancel && (
        <Button size="sm" color="outline-secondary" onClick={onCancel} disabled={isUpdating}>
          Cancel
        </Button>
      )}
    </div>
  )
}

export default TenantMappingPicker
